import OpenAI from 'openai';

// Prefer the Vercel-provided env var; fall back to legacy names for local dev safety.
const apiKey =
  import.meta.env.VITE_OPENAI_API_KEY ??
  import.meta.env.VITE_OPENAI ??
  import.meta.env.OPENAI_API_KEY;

if (!apiKey) {
  console.warn('OpenAI API key not found. Set VITE_OPENAI_API_KEY in your environment (e.g., Vercel).');
}

export const openai = apiKey
  ? new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true // Note: In production, this should be handled server-side
    })
  : null;

const OPENAI_CHAT_URL = 'https://api.openai.com/v1/chat/completions';

const isConnectionLikeError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message.toLowerCase() : String(error || '').toLowerCase();
  return (
    msg.includes('connection error') ||
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('load failed')
  );
};

const formatOpenAIError = (error: unknown): string => {
  if (!isConnectionLikeError(error)) {
    return error instanceof Error ? error.message : 'Unknown error';
  }

  return [
    'Connection error reaching OpenAI.',
    'Check internet/firewall, disable ad-block/privacy extensions for this site, and verify your API key is valid.',
    'If deployed, ensure VITE_OPENAI_API_KEY (or VITE_OPENAI) is set at build time.'
  ].join(' ');
};

const createChatCompletion = async (params: OpenAI.Chat.Completions.ChatCompletionCreateParamsNonStreaming) => {
  if (!openai || !apiKey) {
    throw new Error('OpenAI API not configured');
  }

  try {
    return await openai.chat.completions.create(params);
  } catch (sdkError) {
    // Browser + older SDK versions can report a generic "Connection error"
    // even when direct fetch succeeds. Retry once via raw fetch.
    if (!isConnectionLikeError(sdkError)) {
      throw sdkError;
    }

    const fallbackResponse = await fetch(OPENAI_CHAT_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    });

    let payload: any = null;
    try {
      payload = await fallbackResponse.json();
    } catch (_) {
      payload = null;
    }

    if (!fallbackResponse.ok) {
      const apiMessage =
        payload?.error?.message ||
        `HTTP ${fallbackResponse.status}${fallbackResponse.statusText ? ` ${fallbackResponse.statusText}` : ''}`;
      throw new Error(apiMessage);
    }

    return payload;
  }
};

export interface ExtractedData {
  demographics?: any;
  personalValues?: any;
  brandAffinity?: any;
  psychologicalDrivers?: any;
  hobbiesAndHabits?: any;
  dailyRoutine?: any;
  customCharts?: any;
  sections?: any;
  insights?: any;
  error?: string;
}

// Accept either plain text content or a list of mixed content parts (text and image urls)
type ContentPart = { type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string } };

const tryParseJson = (candidate: string): any | undefined => {
  if (!candidate) return undefined;
  try {
    return JSON.parse(candidate);
  } catch (jsonError) {
    try {
      // Fallback: allow slightly non-JSON syntax (single quotes, trailing commas)
      /* eslint-disable no-new-func */
      return new Function(`return (${candidate});`)();
      /* eslint-enable no-new-func */
    } catch (fnError) {
      return undefined;
    }
  }
};

const extractJsonCandidates = (content: string): string[] => {
  const candidates: string[] = [];
  if (!content) return candidates;

  // Prefer fenced code blocks labelled json (or any fence)
  const codeBlockRegex = /```(?:json)?\s*([\s\S]*?)```/gi;
  let blockMatch: RegExpExecArray | null;
  while ((blockMatch = codeBlockRegex.exec(content))) {
    const candidate = blockMatch[1]?.trim();
    if (candidate) candidates.push(candidate);
  }

  // Fallback: scan for balanced JSON objects/arrays in the raw text.
  let stringDelimiter: string | null = null;
  let escape = false;
  let startIndex: number | null = null;
  const stack: string[] = [];

  for (let i = 0; i < content.length; i++) {
    const char = content[i];

    if (escape) {
      escape = false;
      continue;
    }

    if (stringDelimiter) {
      if (char === '\\') {
        escape = true;
      } else if (char === stringDelimiter) {
        stringDelimiter = null;
      }
      continue;
    }

    if (char === '"' || char === '\'' || char === '`') {
      stringDelimiter = char;
      continue;
    }

    if (char === '{' || char === '[') {
      if (stack.length === 0) startIndex = i;
      stack.push(char);
      continue;
    }

    if (char === '}' || char === ']') {
      if (stack.length === 0) continue;
      const opening = stack.pop();
      if (!opening) continue;
      if ((opening === '{' && char !== '}') || (opening === '[' && char !== ']')) {
        stack.length = 0;
        startIndex = null;
        continue;
      }
      if (stack.length === 0 && startIndex !== null) {
        const candidate = content.slice(startIndex, i + 1).trim();
        if (candidate) candidates.push(candidate);
        startIndex = null;
      }
    }
  }

  if (candidates.length === 0) {
    const trimmed = content.trim();
    if (trimmed) candidates.push(trimmed);
  }

  return candidates;
};

const stripWrapper = (value: string): string => {
  const trimmed = value.trim();
  const firstBrace = trimmed.search(/[\[{]/);
  if (firstBrace > 0) {
    return trimmed.slice(firstBrace);
  }
  return trimmed;
};

const stripTrailingContent = (value: string): string => {
  let trimmed = value.trim();
  const lastBrace = trimmed.lastIndexOf('}');
  const lastBracket = trimmed.lastIndexOf(']');
  const lastIndex = Math.max(lastBrace, lastBracket);
  if (lastIndex >= 0 && lastIndex < trimmed.length - 1) {
    trimmed = trimmed.slice(0, lastIndex + 1);
  }
  return trimmed;
};

const sanitizeJsonCandidate = (candidate: string): string[] => {
  const variants = new Set<string>();
  const push = (val: string | undefined) => {
    if (!val) return;
    const normalized = val.trim();
    if (normalized) variants.add(normalized);
  };

  const base = candidate.trim();
  push(base);
  push(stripWrapper(base));
  push(stripTrailingContent(base));

  const unwrapped = base
    .replace(/^export\s+default\s+/i, '')
    .replace(/^module\.exports\s*=\s*/i, '')
    .replace(/^const\s+[A-Za-z0-9_$]+\s*=\s*/i, '')
    .replace(/^let\s+[A-Za-z0-9_$]+\s*=\s*/i, '')
    .replace(/^var\s+[A-Za-z0-9_$]+\s*=\s*/i, '')
    .replace(/^return\s+/i, '');
  push(stripWrapper(unwrapped));

  const normalizedQuotes = base
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"');
  push(normalizedQuotes);
  push(stripWrapper(normalizedQuotes));
  push(stripTrailingContent(normalizedQuotes));

  const noTrailingCommas = normalizedQuotes.replace(/,\s*([}\]])/g, '$1');
  push(noTrailingCommas);

  const commentStripped = normalizedQuotes
    .replace(/^\s*<!--([\s\S]*?)-->\s*$/g, '$1')
    .replace(/\/\/.*$/gm, '')
    .replace(/\/\*[\s\S]*?\*\//g, '');
  push(commentStripped);
  push(stripWrapper(commentStripped));
  push(stripTrailingContent(commentStripped));

  const boolNormalized = commentStripped
    .replace(/\bTrue\b/g, 'true')
    .replace(/\bFalse\b/g, 'false')
    .replace(/\bNull\b/g, 'null');
  push(boolNormalized);
  push(stripWrapper(boolNormalized));
  push(stripTrailingContent(boolNormalized));

  if (boolNormalized.includes("'")) {
    const singleToDouble = boolNormalized.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, '"$1"');
    push(singleToDouble);
    push(stripWrapper(singleToDouble));
    push(stripTrailingContent(singleToDouble));
  }

  return Array.from(variants.values());
};

const extractFirstJsonPayload = (content: string): any | undefined => {
  const candidates = extractJsonCandidates(content);
  for (const candidate of candidates) {
    const variants = sanitizeJsonCandidate(candidate);
    for (const variant of variants) {
      const parsed = tryParseJson(variant);
      if (parsed !== undefined) {
        return parsed;
      }
    }
  }
  return undefined;
};

// Note: clientId is optional so dev tools/tests can call without persistence
export async function extractDataFromFile(
  fileContent: string | ContentPart[],
  fileType: string,
  clientId?: string
): Promise<ExtractedData> {
  if (!openai) {
    return { error: 'OpenAI API not configured' };
  }

  try {
    const instructions = `You are an expert data analyst and React developer. A user will provide ${fileType} content via separate messages (text, images, or both). You must respond with VALID JSON that strictly follows the schema below. Do not include any extra commentary, markdown, \\u201ccode fences\\u201d, or explanations.

Absolute requirements:
1) Extract EVERY number, percentage, label, tick, legend entry, footnote, and axis value present in the source. Do not summarize or omit. If a value is unreadable, include null and add a note.
2) Preserve chart structure: if the source shows categories or series (e.g., gender, age buckets), reproduce all of them with exact labels.
3) Return both the raw data and a React component that renders it beautifully with Tailwind CSS. Use small icons where relevant (emoji or inline SVG). No imports.
4) Components must not import packages. Use plain React + Tailwind and inline SVG/icon placeholders if needed.
5) Always handle empty/missing data by rendering a friendly "No data available" state.
6) For any requested data shape (like Audience, Education, etc.), if a specific field or label is present in the source image but has no value, you MUST return that field with a value of 0.

STRICT CANONICAL TITLES AND KEYS
When the source matches any of the following categories, use the EXACT title and data shape shown:

- Audience
  data: {
    gender?: { "Male": number, "Female": number }, // if only one is present, also include the other as 100 - present
    age?: { "18-24": number, "25-34": number, "35-44": number, "45-49": number, "50-54": number, "55-64": number, "65+": number },
    // Age charts often show AUDIENCE (teal/blue) and baseline (gray). Capture the AUDIENCE percentage for each bucket. If no audience bar is present, set that bucket to 0 (not the gray baseline value). Keep buckets in the exact order above.
    income?: { "<$25k": number, "$25k-50k": number, "$50k-75k": number, "$75k-100k": number, "$100k-150k": number, "$150k-200k": number, "$200k-250k": number, "$250k+": number },
    marital?: { "Single": number, "Married": number, "Divorced": number, "Widow/Widower": number },
    children?: { "No Children": number, "1 Child": number, "2 or 3 Children": number, "4 or 5 Children": number, "6+ Children": number }
  },
  metrics?: { projectedAdultPopulation?: string | number }

- Education
  data: {
    "Less Than High School Degree": number,
    "High School Degree": number,
    "Some College": number,
    "College Degree": number,
    "Post-graduate Degree": number
  }
  Notes: These charts show AUDIENCE (teal/blue) vs baseline (gray). Return the AUDIENCE percentages only; if the AUDIENCE bar is missing, use 0. Keep the order exactly as listed above.

- Employment Status
  data: {
    [statusLabel: string]: { index: number, composition: number }
  }
  Notes: Include *every* status visible (e.g., Self-Employed, Full-Time, Part-Time, Retired). Do not collapse to a single “FULL-TIME” entry. Each status must have both index and composition numbers, even if you have to set composition to null when unreadable.

- Personal Values
  data: [
    {
      label: one of ["Nature","Creativity","Stimulation","Independence","Pleasure","Equality","Reputation","Achievement","Authority","Influence","Safety","Tradition","Conformity","Caring","Tolerance","Dependability","Humility","Duty","Security"],
      value: number,
      longTitle: string,        // The full descriptor/subtitle exactly as printed in the source (e.g., "Safety in Community and Nation")
      description: string,      // The explanatory paragraph that follows the heading
      tip: string               // Any "Tip" / "Consider" / guidance text associated with the value
    }
  ]
  Notes: Never repeat the short value label inside longTitle. If the source lacks any of these fields, return null for that property.

- Psychological Drivers
  data: [ { "insight": string, "index": number, "composition": number } ... ]
  Also populate the top-level key "psychologicalDrivers" with this array.

- Hobbies & Habits
  data: [ { "insight": string, "index": number, "composition": number } ... ]
  Also populate the top-level key "hobbiesAndHabits" with this array.

- Daily Routine
  data: [ { "insight": string, "index": number, "composition": number } ... ]
  Also populate the top-level key "dailyRoutine" with this array.

- Brand Affinity
  data: {
    Retailers?: [ { label: string, index: number, composition: number }... ],
    Restaurants?: [ { label: string, index: number, composition: number }... ],
    Hotels?: [ { label: string, index: number, composition: number }... ],
    Airlines?: [ { label: string, index: number, composition: number }... ],
    Auto?: [ { label: string, index: number, composition: number }... ]
  },
  metrics?: { projectedAdultPopulation?: string | number }

- Site Affinity
  data should be a list of rows for websites/publishers. Use this flexible but canonical form:
  [
    { "Domain Name": string, "Category"?: string, "UV Index"?: number, "QVI Audience"?: number }
  ]
 - Category Affinity
  data should be a list of rows for categories/segments. Flexible form:
  [
    { "Category": string, "Index"?: number, "Affinity"?: number, "Score"?: number }
  ]
  Provide at least one numeric metric among {Index, Affinity, Score}.
 
 - AEO/GEO/SEO
  This section contains a bubble chart titled similar to "Share of Voice vs. Sentiment" with multiple brands/competitors.
  Return the data in this canonical form:
  {
    "points": [
      { "name": string, "shareOfVoice": number, "sentiment": number, "size"?: number, "sizeCategory"?: "X-Large" | "Large" | "Medium" | "Small" | "X-Small", "color"?: string }
    ],
    "opportunitiesText"?: string
  }
  Where x-axis is shareOfVoice (%), y-axis is sentiment score (%). Return both a numeric size if derivable and a categorical size bucket among {X-Large, Large, Medium, Small, X-Small}. Match legend/dot colors from the source and include the exact HEX color string in "color". Do not invent points; include every visible brand. If any value is unreadable, set null and note.
  Notes:
  - Always include "Domain Name".
  - Provide at least one numeric field among {"UV Index", "QVI Audience", "Index", "Affinity", "Score"}.
  - Do not synthesize values; if unavailable, omit the field.

Return JSON in this exact shape:
{
  "sections": [
    {
      "title": "Section Title",
      "order": 1,
      "data": { /* FULL raw data with every value present in source */ },
      "metrics": { "projectedAdultPopulation": "optional human-readable value like 236K*" },
      "componentCode": "function CustomComponent({ data, title }) {\n  // Renders every data point; includes badges/icons where appropriate\n  return (\n    <div className='bg-white rounded-lg shadow-md p-6'>\n      <h3 className='text-xl font-bold mb-4'>{title}</h3>\n      {/* Example patterns: tables, bars, badges, progress bars; no external imports */}\n    </div>\n  );\n}",
      "componentProps": { "title": "Section Title" }
    }
  ],
  "insights": [ { "title": "...", "summary": "...", "confidence": 0.95 } ]
}

If some expected datasets are not present in the source, simply omit those sections. Do not invent values.`;

    const messages: OpenAI.Chat.Completions.ChatCompletionMessageParam[] = [
      { role: 'system', content: instructions }
    ];

    const userParts = Array.isArray(fileContent)
      ? (fileContent as ContentPart[])
      : ([{ type: 'text', text: String(fileContent) }] as ContentPart[]);

    // Prepend clarifying text for the model about the file type being analyzed.
    userParts.unshift({ type: 'text', text: `Analyze this ${fileType} content and respond with the required JSON schema.` });

    messages.push({ role: 'user', content: userParts as any });

    const response = await createChatCompletion({
      model: 'gpt-4o',
      response_format: { type: 'json_object' },
      messages,
      temperature: 0.1,
      max_tokens: 4000
    });

    const content = response.choices[0]?.message?.content || '';
    if (!content) {
      return { error: 'No response from OpenAI' };
    }

    try {
      // With response_format=json_object we expect parseable JSON directly.
      try {
        const directParsed = JSON.parse(content);
        if (directParsed) {
          const extractedData = Array.isArray(directParsed)
            ? { sections: directParsed }
            : directParsed;

          if (clientId) {
            await saveDemographicsToSupabase(clientId, extractedData);
          }

          return extractedData;
        }
      } catch (directError) {
        // Fall through to heuristic parsing below
      }

      const parsedJson = extractFirstJsonPayload(content);
      if (!parsedJson) {
        console.error('OpenAI response did not contain valid JSON:', content);
        return { error: 'No valid JSON found in response' };
      }
      let extractedData: ExtractedData;

      // If the root is an array of sections, wrap it in the expected object structure.
      if (Array.isArray(parsedJson)) {
        extractedData = { sections: parsedJson };
      } else {
        extractedData = parsedJson;
      }

      // Try to persist demographics whenever we have a client id
      if (clientId) {
        await saveDemographicsToSupabase(clientId, extractedData);
      }
      
      return extractedData;
    } catch (parseError) {
      console.error('Failed to parse JSON from OpenAI response:', parseError, content);
      return { error: 'Failed to parse extracted data as JSON. Check console for raw output.' };
    }
  } catch (error) {
    console.error('OpenAI extraction error:', error);
    return { error: `OpenAI API error: ${formatOpenAIError(error)}` };
  }
}

export async function extractTextFromImage(imageUrl: string): Promise<string> {
  if (!openai) {
    throw new Error('OpenAI API not configured');
  }

  try {
    const response = await createChatCompletion({
      model: 'gpt-4o',
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: 'Extract all text and data from this image. Focus on charts, tables, and statistical information.'
            },
            {
              type: 'image_url',
              image_url: { url: imageUrl }
            }
          ]
        }
      ],
      max_tokens: 4000
    });

    return response.choices[0]?.message?.content || '';
  } catch (error) {
    console.error('Image text extraction error:', error);
    throw error;
  }
}

import { supabase } from './supabase';
import { AuthService } from '../services/authService';

// --- Robust demographics persistence (tolerant of title/key variations) ---
export async function saveDemographicsToSupabase(clientId: string, data: any) {
  if (!clientId || !data) return;

  // Helpers
  const toNum = (v: any): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const cleaned = v.replace(/[^0-9.\-]/g, '');
      if (!cleaned) return null;
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };
  const norm = (s: any): string => String(s || '').toLowerCase().replace(/[^a-z0-9+]+/g, ' ').trim();

  const pick = (obj: any, keys: string[]): Record<string, number> => {
    const out: Record<string, number> = {};
    keys.forEach((k) => {
      const v = toNum(obj?.[k]) ?? toNum(obj?.[k.toLowerCase()]);
      if (v !== null) out[k] = v;
    });
    return out;
  };

  const findSection = (titles: string[]): any | undefined => {
    const arr = Array.isArray(data?.sections) ? data.sections : [];
    return arr.find((s: any) => titles.some((t) => norm(s?.title) === norm(t) || norm(s?.title).includes(norm(t))));
  };

  // Derive sources from either sections or direct payloads
  const audience = findSection(['Audience', 'Demographics', 'Demographic Highlights'])?.data || data.demographics || data.audience || data.data?.audience || data.data?.demographics;
  const education = (findSection(['Education'])?.data) || data.education || data.data?.education;
  const employment = (findSection(['Employment Status', 'Employment'])?.data) || data.employment || data.data?.employment;

  // Gender
  const genderSrc = audience?.gender || audience?.Gender || audience;
  const gender = pick(genderSrc, ['Male', 'Female']);
  if (Object.keys(gender).length === 0 && genderSrc) {
    // try lowercase keys
    const male = toNum(genderSrc.male);
    const female = toNum(genderSrc.female);
    if (male !== null || female !== null) {
      gender.Male = male ?? 0;
      gender.Female = female ?? 0;
    }
  }

  // Age groups
  const ageSrc = audience?.age || audience?.ageGroups || audience?.ages || audience;
  const ages = pick(ageSrc, ['18-24', '25-34', '35-44', '45-49', '50-54', '55-64', '65+']);

  // Education buckets
  const eduSrc = education || audience?.education || {};
  const edu = {
    less_than_high_school_degree: toNum(eduSrc['Less Than High School Degree'] ?? eduSrc.lessThanHighSchool) ?? undefined,
    high_school_degree: toNum(eduSrc['High School Degree'] ?? eduSrc.highSchool) ?? undefined,
    some_college: toNum(eduSrc['Some College'] ?? eduSrc.someCollege) ?? undefined,
    college_degree: toNum(eduSrc['College Degree'] ?? eduSrc.collegeDegree) ?? undefined,
    post_grad_degree: toNum(eduSrc['Post-graduate Degree'] ?? eduSrc.postGrad) ?? undefined,
  };

  // Employment compositions
  const empSrc = employment || audience?.employment || {};
  const emp = {
    self_employed: toNum(empSrc['Self-Employed']?.composition ?? empSrc.selfEmployed ?? empSrc['self-employed']) ?? undefined,
    full_time: toNum(empSrc['Full-Time']?.composition ?? empSrc.fullTime ?? empSrc['full-time']) ?? undefined,
    part_time: toNum(empSrc['Part-Time']?.composition ?? empSrc.partTime ?? empSrc['part-time']) ?? undefined,
    unemployed: toNum(empSrc['Unemployed']?.composition ?? empSrc.unemployed) ?? undefined,
  };

  const execUpsertVariants = async (table: string, payloads: Array<Record<string, any>>) => {
    const session = AuthService.getSession();
    if (!session || !session.token) {
      console.warn(`Skipping upsert to ${table}: no active admin session`);
      return;
    }

    let lastError: any = null;
    for (const payload of payloads) {
      const { error } = await supabase.functions.invoke('admin-client-write', {
        headers: { 'x-admin-session': session.token },
        body: {
          operation: 'upsert',
          table,
          data: payload,
          onConflict: 'client_id',
        },
      });
      if (!error) return;
      lastError = error;
    }
    if (lastError) {
      console.error(`Failed to upsert ${table}:`, lastError);
      throw lastError;
    }
  };

  if (Object.keys(gender).length > 0) {
    const male = toNum(gender.Male) ?? toNum((gender as any).male) ?? 0;
    const female = toNum(gender.Female) ?? toNum((gender as any).female) ?? 0;
    await execUpsertVariants('client_gender_data', [
      { client_id: clientId, male, female },
    ]);
  }

  if (Object.keys(ages).length > 0) {
    const canonical = {
      '18-24': toNum(ages['18-24']) ?? 0,
      '25-34': toNum(ages['25-34']) ?? 0,
      '35-44': toNum(ages['35-44']) ?? 0,
      '45-49': toNum(ages['45-49']) ?? 0,
      '50-54': toNum(ages['50-54']) ?? 0,
      '55-64': toNum(ages['55-64']) ?? 0,
      '65+': toNum(ages['65+']) ?? 0,
    };

    const withHyphenKeys = { client_id: clientId, ...canonical };
    const withSnakeKeys = {
      client_id: clientId,
      '18_24': canonical['18-24'],
      '25_34': canonical['25-34'],
      '35_44': canonical['35-44'],
      '45_49': canonical['45-49'],
      '50_54': canonical['50-54'],
      '55_64': canonical['55-64'],
      '65_plus': canonical['65+'],
    };
    const withPrefixedKeys = {
      client_id: clientId,
      age_18_24: canonical['18-24'],
      age_25_34: canonical['25-34'],
      age_35_44: canonical['35-44'],
      age_45_49: canonical['45-49'],
      age_50_54: canonical['50-54'],
      age_55_64: canonical['55-64'],
      age_65_plus: canonical['65+'],
    };

    await execUpsertVariants('client_age_group_data', [withHyphenKeys, withSnakeKeys, withPrefixedKeys]);
  }

  if (Object.values(edu).some((v) => v !== undefined)) {
    const canonical = {
      less_than_high_school_degree: edu.less_than_high_school_degree ?? 0,
      high_school_degree: edu.high_school_degree ?? 0,
      some_college: edu.some_college ?? 0,
      college_degree: edu.college_degree ?? 0,
      post_grad_degree: edu.post_grad_degree ?? 0,
    };

    const payloads = [
      { client_id: clientId, ...canonical },
      {
        client_id: clientId,
        less_than_high_school: canonical.less_than_high_school_degree,
        high_school: canonical.high_school_degree,
        some_college: canonical.some_college,
        college_degree: canonical.college_degree,
        post_grad_degree: canonical.post_grad_degree,
      },
    ];

    await execUpsertVariants('client_education_data', payloads);
  }

  if (Object.values(emp).some((v) => v !== undefined)) {
    const canonical = {
      self_employed: emp.self_employed ?? 0,
      full_time: emp.full_time ?? 0,
      part_time: emp.part_time ?? 0,
      unemployed: emp.unemployed ?? 0,
    };

    const payloads = [
      { client_id: clientId, ...canonical },
      {
        client_id: clientId,
        self_employed: canonical.self_employed,
        full_time: canonical.full_time,
        part_time: canonical.part_time,
        unemployed: canonical.unemployed,
        self_employed_composition: canonical.self_employed,
        full_time_composition: canonical.full_time,
      },
      {
        client_id: clientId,
        self_employed_percent: canonical.self_employed,
        full_time_percent: canonical.full_time,
        part_time_percent: canonical.part_time,
        unemployed_percent: canonical.unemployed,
      },
    ];

    await execUpsertVariants('client_employment_data', payloads);
  }
}
